/**
 * Models for the Consent Management feature.
 *
 * Mirrors the response of `GET /forms/org/{org_id}/latest-published`, which returns
 * the latest *published* consent form for an organisation.
 *
 * NOTE: this endpoint's envelope uses a string `status` ("success") and does NOT
 * match the generic `APIResponseModel` (whose `status` is boolean), so it
 * intentionally has its own typed envelope below.
 */

export interface ConsentDataField {
  id: string;
  name: string;
  type: string;
  required: boolean;
}

/** Lawful basis for processing; kept open-ended to tolerate backend additions. */
export type ConsentLegalBasis =
  | 'consent'
  | 'contract'
  | 'legal_obligation'
  | 'legitimate_interest'
  | (string & {});

/** Data-subject rights advertised by the consent form. */
export type ConsentUserRight = 'withdraw' | 'access' | 'delete' | (string & {});

export interface ConsentFormData {
  id: string;
  name: string;
  code: string;
  org_id: string;
  owner: string;
  status: string;
  active_version: string;
  purpose_short: string;
  created_at: string;
  updated_at: string;
  version_id: string;
  version: string;
  title: string;
  description: string;
  purpose: string;
  legal_basis: ConsentLegalBasis;
  retention_days: number;
  expiry_days: number;
  third_parties: string[] | null;
  user_rights: ConsentUserRight[];
  data_fields: ConsentDataField[];
  /**
   * Admin-authored, full HTML document for the consent UI. Treated as UNTRUSTED:
   * rendered inside a sandboxed iframe, never injected into the app DOM.
   */
  custom_body: string;
  published_at: string;
  published_by: string;
}

export interface ConsentFormResponse {
  status: 'success' | (string & {});
  data: ConsentFormData;
  message: string;
}

/** Result emitted when the consent dialog closes. */
export interface ConsentDialogResult {
  accepted: boolean;
}

/** Data handed to {@link ConsentFormDialogComponent} via PrimeNG's DynamicDialogConfig. */
export interface ConsentFormDialogData {
  form: ConsentFormData;
}

// ──────────────────────────────────────────────────────────────────────────────
// Accept API
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Body for "record consent acceptance". IDs are sourced from HOST-owned state
 * (the fetched form + the authenticated profile), never from the iframe — the
 * iframe only signals intent. Auth is the `X-API-Key` header, not a bearer.
 */
export interface ConsentAcceptRequest {
  external_user_id: string;
  form_id: string;
  version: string;
}

export interface ConsentAcceptResponse {
  status: 'success' | (string & {});
  message: string;
  data?: unknown;
}

// ──────────────────────────────────────────────────────────────────────────────
// Status API
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Result of `GET /v1/public/consents/status`. The backend does the
 * decision-making (validity, expiry, version reconsent) and collapses it into a
 * single `status` boolean: `true` means the user is fully consented to the
 * active version (show nothing); `false` means consent is still owed (prompt).
 * The remaining fields are metadata about the existing consent, present only
 * once the user has consented.
 */
export interface ConsentStatusData {
  status: boolean;
  consent_id: string | null;
  form_id: string | null;
  active_version: string | null;
  user_consented_version: string | null;
  granted_at: string | null;
  expires_at: string | null;
}

export interface ConsentStatusResponse {
  status: 'success' | (string & {});
  data: ConsentStatusData;
  message: string;
}
